import { EmptyState } from '@/components/EmptyState'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import ReactECharts from 'echarts-for-react'
import { useAtomValue } from 'jotai'
import { ChartSpline } from 'lucide-react'
import { forwardRef } from 'react'
import { CATEGORY_COLOURS, FALLBACK_COLOUR } from '../constants/riskProfile.constants'
import { useChartDisplayRange } from '../hooks/useChartDisplayRange'
import type { RiskProfileMetricOption } from '../types/riskProfile.types'
import { formatRiskProfileValue } from '../utils/riskProfile.utils'
import { ChartDisplayRange } from './ChartDisplayRange'
import { mapRiskProfileResponseToAreaRanking } from './RiskProfileChartData'

const truncateLabel = (label: string, maxLength = 46): string =>
  label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label

export interface RiskByElementsChartProps {
  data: Parameters<typeof mapRiskProfileResponseToAreaRanking>[0]
  metricOption: RiskProfileMetricOption
  onItemClick?: (id: string) => void
  useAvgPercentValue?: boolean
}

const BAR_MAX_WIDTH = 18

const RiskByElementsChart = forwardRef<ReactECharts, RiskByElementsChartProps>(
  ({ data, metricOption, onItemClick, useAvgPercentValue = false }, ref) => {
    const { showOption, setShowOption, topN } = useChartDisplayRange(data.group_totals.length)
    const currencySymbol = useAtomValue(currencySymbolAtom)
    const defaultUnit =
      metricOption.metric === 'damage_cost' || metricOption.metric === 'replacement_cost'
        ? currencySymbol
        : null

    const chartData = mapRiskProfileResponseToAreaRanking(data, {
      metricOption,
      topN,
      useAvgPercentValue,
    })
    const showAvgPercent =
      useAvgPercentValue && metricOption.metric === 'quantity' && metricOption.type === 'percentage'

    if (chartData.areaLabels.length === 0) {
      return (
        <EmptyState
          title=""
          description="No comparable element types for this selection."
          showButton={false}
          icon={<ChartSpline className="size-8 stroke-rexBlue" />}
        />
      )
    }

    const labelToGroupKey = new Map(data.groups.map((group) => [group.label, group.key]))
    const groupKeyToTotal = new Map(
      data.group_totals.map((total) => [
        total.group_key,
        {
          value: total.value,
          unit: total.unit,
          percent_value: total.percent_value,
          avg_percent_value: total.avg_percent_value ?? null,
        },
      ]),
    )

    const elements = chartData.areaLabels.map((label) => ({
      id: labelToGroupKey.get(label) ?? label,
      label,
    }))

    const totals = elements.map(({ id }) => {
      const total = id ? groupKeyToTotal.get(id) : undefined
      return total
        ? {
            value: total.value,
            unit: total.unit ?? defaultUnit,
            percent_value: showAvgPercent ? total.avg_percent_value : total.percent_value,
          }
        : { value: 0, unit: defaultUnit, percent_value: 0 }
    })

    const handleElementClick = (params: { componentType: string; value?: string }) => {
      if (params.componentType !== 'yAxis' || !params.value) return
      onItemClick?.(params.value)
    }

    const handleElementMouseOver = (params: { componentType: string }) => {
      if (params.componentType === 'yAxis' && onItemClick) {
        document.body.style.cursor = 'pointer'
      }
    }

    const handleElementMouseOut = () => {
      document.body.style.cursor = 'default'
    }

    const elementById = new Map(elements.map((element) => [element.id, element]))

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (value: number) => formatRiskProfileValue(value, metricOption, defaultUnit),
      },
      legend: {
        top: 0,
        left: 0,
        show: false,
      },
      grid: { left: 0, right: 80, top: 16, bottom: 32, containLabel: false },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatRiskProfileValue(value, metricOption, defaultUnit),
          align: 'left',
        },
        splitNumber: 4,
      },
      yAxis: {
        type: 'category',
        inverse: true,
        triggerEvent: onItemClick !== undefined,
        data: elements.map((element) => element.id),
        axisLabel: {
          align: 'right',
          width: 140,
          margin: 12,
          lineHeight: 16,
          formatter: (elementId: string) => {
            const element = elementById.get(elementId)
            if (!element) return ''
            return onItemClick ? `${element.label} ›` : element.label
          },
          color: onItemClick ? '#006fd6' : undefined,
        },
      },
      series: chartData.series.map((series, seriesIndex) => ({
        name: series.name,
        type: 'bar',
        stack: 'total',
        data: series.values,
        barMaxWidth: BAR_MAX_WIDTH,
        itemStyle: { color: CATEGORY_COLOURS[series.name] ?? FALLBACK_COLOUR },
        label:
          seriesIndex === chartData.series.length - 1
            ? {
                show: true,
                position: 'right',
                formatter: (params: { dataIndex: number }) =>
                  formatRiskProfileValue(
                    metricOption.type === 'total'
                      ? (totals[params.dataIndex].value ?? 0)
                      : (totals[params.dataIndex].percent_value ?? 0),
                    metricOption,
                    totals[params.dataIndex].unit,
                  ),
                color: '#000',
                fontWeight: 'bold',
              }
            : undefined,
      })),
    }

    return (
      <div>
        <ChartDisplayRange
          shown={chartData.areaLabels.length}
          total={data.group_totals.length}
          value={showOption}
          onChange={setShowOption}
        />

        <ReactECharts
          ref={ref}
          option={option}
          notMerge
          onEvents={{
            click: handleElementClick,
            mouseover: handleElementMouseOver,
            mouseout: handleElementMouseOut,
          }}
          style={{ height: Math.max(280, chartData.areaLabels.length * 36 + 80) }}
        />
      </div>
    )
  },
)

export default RiskByElementsChart
